import { generateActuatorControlName } from "../../utils/actuatorIds";
import type { Actuator, Joint, JointUUID } from "../../types/scene";
import { formatMjcfNumber } from "./xmlGenerationHelpers";

type ExportActuator = Actuator & {
  class?: string;
  site?: string;
  refsite?: string;
  plugin?: string;
  instance?: string;
  actdim?: number | string;
  gaintype?: string;
  biastype?: string;
  kp?: number | string;
  kv?: number | string;
  dampratio?: number | string;
};

type JointExportInfo = {
  uuid?: string;
  name?: string;
  kind?: string | null;
  bodyUuid?: string | null;
  rootUuid?: string | null;
};
type SiteExportInfo = {
  uuid?: unknown;
  name?: unknown;
};
type BodyExportInfo = {
  uuid?: unknown;
  name?: unknown;
};
type XmlLogger = { warn: (...args: unknown[]) => void };

const KNOWN_ACTUATOR_TAGS = new Set([
  "general",
  "position",
  "velocity",
  "intvelocity",
  "intposition",
  "spring",
  "damper",
  "motor",
  "cylinder",
  "muscle",
  "plugin",
]);

export const resolveActuatorTagName = (actuator: Partial<Actuator> | null | undefined): string => {
  const kind = String(actuator?.kind || "")
    .trim()
    .toLowerCase();
  if (!kind) return "general";
  return KNOWN_ACTUATOR_TAGS.has(kind) ? kind : "general";
};

/** Only `<general>` accepts raw gainprm/biasprm/gaintype/biastype in MJCF; shortcut tags reject them and trigger mjXError. */
export const mayEmitStoredGainBiasInXml = (actuatorTag: string): boolean =>
  actuatorTag === "general";

export const resolveActuatorClassAttr = (
  actuatorTag: string,
  actuator: ExportActuator | null | undefined,
  fallbackClass: string | null
): string => {
  const explicitClass = typeof actuator?.class === "string" ? actuator.class.trim() : "";
  if (explicitClass) return ` class="${explicitClass}"`;
  if (actuatorTag === "general" && fallbackClass) return ` class="${fallbackClass}"`;
  return "";
};

export const appendActivationDynamicsAttrs = ({
  attrs,
  actuator,
  arrayToString,
  xmlEscapeAttr,
}: {
  attrs: string;
  actuator: ExportActuator;
  arrayToString: (values: Array<number | string>) => string;
  xmlEscapeAttr: (value: string) => string;
}): string => {
  let next = attrs;
  const dyntype =
    typeof actuator?.dyntype === "string" && actuator.dyntype.trim().length > 0
      ? actuator.dyntype.trim()
      : "";
  if (dyntype) next += ` dyntype="${xmlEscapeAttr(dyntype)}"`;
  const dynprm = Array.isArray(actuator?.dynprm) ? actuator.dynprm.map(Number) : null;
  if (dynprm && dynprm.length > 0 && dynprm.every(Number.isFinite)) {
    next += ` dynprm="${arrayToString(dynprm)}"`;
  }
  const actrange = Array.isArray(actuator?.actrange) ? actuator.actrange.map(Number) : null;
  if (actrange && actrange.length === 2 && actrange.every(Number.isFinite)) {
    next += ` actrange="${arrayToString(actrange)}"`;
  }
  const actlimited =
    actuator?.actlimited !== undefined && actuator?.actlimited !== null
      ? String(actuator.actlimited).trim()
      : "";
  if (actlimited) next += ` actlimited="${xmlEscapeAttr(actlimited)}"`;
  const actearly = Number(actuator?.actearly);
  if (Number.isFinite(actearly)) {
    next += ` actearly="${formatMjcfNumber(actearly)}"`;
  }
  return next;
};

const appendPluginAttrs = ({
  attrs,
  actuator,
  xmlEscapeAttr,
}: {
  attrs: string;
  actuator: ExportActuator;
  xmlEscapeAttr: (value: string) => string;
}): string => {
  let next = attrs;
  const plugin = typeof actuator?.plugin === "string" ? actuator.plugin.trim() : "";
  const instance = typeof actuator?.instance === "string" ? actuator.instance.trim() : "";
  const actdim = Number(actuator?.actdim);
  if (plugin) next += ` plugin="${xmlEscapeAttr(plugin)}"`;
  if (instance) next += ` instance="${xmlEscapeAttr(instance)}"`;
  if (Number.isFinite(actdim)) next += ` actdim="${formatMjcfNumber(actdim)}"`;
  return next;
};

type ExportBodyNode = {
  name?: string;
  children?: ExportBodyNode[];
  joints?: unknown[];
};

type TendonPairForActuator = {
  joint1: string;
  joint2: string;
  tendonName: string;
  actuator?: ExportActuator | null;
};

export const emitActuatorXml = ({
  emitActuators,
  actuatorsArray,
  bodyTreeForExport,
  allBodyNames,
  jointMap,
  siteMap,
  siteExportNameByUuid,
  jointInfoByUuid,
  jointXmlNameMap,
  jointBySanitizedName,
  tendonPairs,
  tendonActuatorsByName,
  tendonUuidToEmittedXmlName,
  tendonRefToEmittedXmlName,
  emittedTendonNames,
  getPreferredExportActuatorName,
  getUniqueActuatorName,
  rememberActuatorExportName,
  isScalarJoint,
  arrayToString,
  sanitizeMjcfName,
  xmlEscapeAttr,
  logger,
}: {
  emitActuators: boolean;
  actuatorsArray: ExportActuator[];
  bodyTreeForExport: ExportBodyNode[];
  allBodyNames: Set<string>;
  jointMap: Record<string, Joint>;
  siteMap: Record<string, SiteExportInfo | undefined>;
  siteExportNameByUuid: Record<string, string>;
  jointInfoByUuid: Map<string, JointExportInfo>;
  jointXmlNameMap: Record<string, string>;
  jointBySanitizedName: Record<string, JointExportInfo | undefined>;
  tendonPairs: TendonPairForActuator[];
  tendonActuatorsByName: Map<string, ExportActuator[]>;
  tendonUuidToEmittedXmlName: Map<string, string>;
  tendonRefToEmittedXmlName: Map<string, string>;
  emittedTendonNames: Set<string>;
  getPreferredExportActuatorName: (
    actuator: { uuid?: string; name?: string } | null | undefined,
    fallbackName: string
  ) => string;
  getUniqueActuatorName: (preferredRaw: string | undefined | null, fallbackRaw: string) => string;
  rememberActuatorExportName: (
    actuator: { uuid?: string; name?: string } | null | undefined,
    exportName: string,
    fallbackName?: string
  ) => void;
  isScalarJoint: (joint: Joint) => boolean;
  arrayToString: (values: Array<number | string>) => string;
  sanitizeMjcfName: (value: string) => string;
  xmlEscapeAttr: (value: string) => string;
  logger: XmlLogger;
}): string => {
  const resolveActuatorSiteXmlName = (siteRef: unknown): string | null => {
    const raw = typeof siteRef === "string" ? siteRef.trim() : "";
    if (!raw) return null;
    if (siteExportNameByUuid[raw]) return siteExportNameByUuid[raw];
    const normalized = sanitizeMjcfName(raw);
    const site = Object.values(siteMap || {}).find((candidate) => {
      if (!candidate) return false;
      const uuid = candidate.uuid ? String(candidate.uuid) : "";
      const name = candidate.name ? String(candidate.name) : "";
      return uuid === raw || name === raw || sanitizeMjcfName(name) === normalized;
    });
    return site?.uuid ? siteExportNameByUuid[String(site.uuid)] || null : null;
  };
  const appendActivationDynamicsAttrsForXml = (attrs: string, actuator: ExportActuator): string =>
    appendActivationDynamicsAttrs({ attrs, actuator, arrayToString, xmlEscapeAttr });
  let actuatorXml = "";
  const actuatorsByJoint = new Map<string, ExportActuator[]>();
  actuatorsArray.forEach((actuator) => {
    if (!actuator) return;
    if (actuator.joint) {
      const jointKey = typeof actuator.joint === "string" ? actuator.joint : "";
      if (jointKey) {
        if (!actuatorsByJoint.has(jointKey)) actuatorsByJoint.set(jointKey, []);
        actuatorsByJoint.get(jointKey)!.push(actuator);
        const jointRef = jointMap[jointKey as keyof typeof jointMap];
        const jointNameCandidate = jointRef?.name;
        if (jointNameCandidate) {
          if (!actuatorsByJoint.has(jointNameCandidate)) {
            actuatorsByJoint.set(jointNameCandidate, []);
          }
          actuatorsByJoint.get(jointNameCandidate)!.push(actuator);
          const sanitizedJoint = sanitizeMjcfName(jointNameCandidate);
          if (!actuatorsByJoint.has(sanitizedJoint)) {
            actuatorsByJoint.set(sanitizedJoint, []);
          }
          actuatorsByJoint.get(sanitizedJoint)!.push(actuator);
        }
      }
    }
  });
  // Only emit actuators for scalar DOF joints (e.g., hinge/slide). Skip free joints.
  // Collect joints from bodies instead of filtering by joint.body
  const activeJoints: Joint[] = [];
  const collectJointsFromBodies = (nodes: ExportBodyNode[]) => {
    nodes.forEach((body) => {
      if (!body || typeof body.name !== "string" || !allBodyNames.has(body.name)) return;
      if (Array.isArray(body.joints)) {
        body.joints.forEach((jointRef) => {
          const joint =
            typeof jointRef === "string"
              ? jointMap[jointRef as JointUUID] || null
              : (jointRef as Joint);
          if (joint && String(joint.kind || "").toLowerCase() !== "free") {
            activeJoints.push(joint);
          }
        });
      }
      if (Array.isArray(body.children)) {
        collectJointsFromBodies(body.children);
      }
    });
  };
  collectJointsFromBodies(bodyTreeForExport);
  if (emitActuators) {
    const actuatorEntries: string[] = [];
    actuatorsArray.forEach((actuator) => {
      if (!actuator || !actuator.site || actuator.joint || actuator.tendon) return;

      const siteName = resolveActuatorSiteXmlName(actuator.site);
      if (!siteName) {
        logger.warn(
          `[xmlGenerationActions] Skipping actuator "${actuator.name || actuator.uuid}" because site "${actuator.site}" was not emitted.`
        );
        return;
      }

      const fallbackGeneratedName = generateActuatorControlName({
        baseName: actuator.name || siteName || "actuator",
        jointUuid: null,
        rootBodyUuid: null,
      });
      const exportName = getUniqueActuatorName(
        getPreferredExportActuatorName(actuator || null, fallbackGeneratedName),
        fallbackGeneratedName
      );
      rememberActuatorExportName(actuator || null, exportName, fallbackGeneratedName);

      const actuatorTag = resolveActuatorTagName(actuator);
      const actuatorClass = actuator.class || null;
      let actuatorAttrs = `name="${exportName}" site="${xmlEscapeAttr(siteName)}"`;
      const refsiteName = resolveActuatorSiteXmlName(actuator.refsite);
      if (refsiteName) {
        actuatorAttrs += ` refsite="${xmlEscapeAttr(refsiteName)}"`;
      }
      actuatorAttrs = appendPluginAttrs({ attrs: actuatorAttrs, actuator, xmlEscapeAttr });

      if (
        actuator.forcerange &&
        Array.isArray(actuator.forcerange) &&
        actuator.forcerange.length === 2
      ) {
        const fr = actuator.forcerange.map(Number);
        if (fr.every(Number.isFinite)) {
          actuatorAttrs += ` forcerange="${arrayToString(fr)}"`;
        }
      }
      if (
        actuator.ctrlrange &&
        Array.isArray(actuator.ctrlrange) &&
        actuator.ctrlrange.length === 2
      ) {
        const cr = actuator.ctrlrange.map(Number);
        if (cr.every(Number.isFinite)) {
          actuatorAttrs += ` ctrlrange="${arrayToString(cr)}"`;
        }
      }
      if (
        mayEmitStoredGainBiasInXml(actuatorTag) &&
        actuator.gainprm &&
        Array.isArray(actuator.gainprm)
      ) {
        actuatorAttrs += ` gainprm="${arrayToString(actuator.gainprm)}"`;
      }
      if (
        mayEmitStoredGainBiasInXml(actuatorTag) &&
        actuator.biasprm &&
        Array.isArray(actuator.biasprm)
      ) {
        actuatorAttrs += ` biasprm="${arrayToString(actuator.biasprm)}"`;
      }
      const explicitGainTypeRaw = actuator.gaintype;
      const explicitBiasTypeRaw = actuator.biastype;
      const explicitGainType =
        typeof explicitGainTypeRaw === "string" && explicitGainTypeRaw.trim().length > 0
          ? explicitGainTypeRaw.trim()
          : "";
      const explicitBiasType =
        typeof explicitBiasTypeRaw === "string" && explicitBiasTypeRaw.trim().length > 0
          ? explicitBiasTypeRaw.trim()
          : "";
      if (mayEmitStoredGainBiasInXml(actuatorTag)) {
        if (explicitGainType) actuatorAttrs += ` gaintype="${explicitGainType}"`;
        if (explicitBiasType) actuatorAttrs += ` biastype="${explicitBiasType}"`;
      }
      const actuatorGear = actuator.gear;
      if (Array.isArray(actuatorGear) && actuatorGear.length > 0) {
        actuatorAttrs += ` gear="${arrayToString(actuatorGear)}"`;
      } else if (typeof actuatorGear === "number" && Number.isFinite(actuatorGear)) {
        actuatorAttrs += ` gear="${formatMjcfNumber(actuatorGear)}"`;
      }
      actuatorAttrs = appendActivationDynamicsAttrsForXml(actuatorAttrs, actuator);

      const classAttr = resolveActuatorClassAttr(actuatorTag, actuator, actuatorClass);
      actuatorEntries.push(`    <${actuatorTag}${classAttr} ${actuatorAttrs}/>\n`);
    });

    // Suppress per-joint actuators for joints that are covered by a tendon pair
    const suppressed = new Set<string>();
    tendonPairs.forEach((pair) => {
      suppressed.add(pair.joint1);
      suppressed.add(pair.joint2);
    });

    // Helper to find which body a joint belongs to
    activeJoints.forEach((joint) => {
      const sanitizedJointName = sanitizeMjcfName(joint.name);
      if (suppressed.has(sanitizedJointName)) {
        return; // skip per-joint actuator for finger pair joints
      }

      // Try to find stored actuators for this joint (there may be more than one)
      const storedActuatorCandidates =
        actuatorsByJoint.get(joint.uuid) ||
        actuatorsByJoint.get(joint.name) ||
        actuatorsByJoint.get(sanitizeMjcfName(joint.name));

      // Only emit explicitly defined actuators; joints without actuator definitions stay passive.
      if (!storedActuatorCandidates || storedActuatorCandidates.length === 0) {
        return;
      }

      const jointInfo = jointInfoByUuid.get(joint.uuid);
      const bodyUuid = jointInfo?.bodyUuid || null;
      const rootUuid = jointInfo?.rootUuid || null;
      const xmlJointName =
        (joint.uuid && jointXmlNameMap[joint.uuid]) ||
        (bodyUuid && joint.uuid && jointXmlNameMap[`${bodyUuid}|${joint.uuid}`]) ||
        (bodyUuid && jointXmlNameMap[`${bodyUuid}|${joint.name}`]) ||
        jointXmlNameMap[joint.name] ||
        sanitizedJointName;

      // Emit one actuator element per explicitly stored actuator.
      const actuatorsToEmit: ExportActuator[] = storedActuatorCandidates;
      actuatorsToEmit.forEach((storedActuator) => {
        const nameSeed = storedActuator.name || sanitizedJointName.replace(/_joint$/i, "");
        const fallbackGeneratedName = generateActuatorControlName({
          baseName: nameSeed,
          jointUuid: joint.uuid,
          rootBodyUuid: rootUuid || null,
        });
        const exportName = getUniqueActuatorName(
          getPreferredExportActuatorName(storedActuator, fallbackGeneratedName),
          fallbackGeneratedName
        );
        rememberActuatorExportName(storedActuator, exportName, fallbackGeneratedName);

        const actuatorClass = storedActuator.class || null;
        const actuatorTag = resolveActuatorTagName(storedActuator);
        const explicitGainTypeRaw = storedActuator.gaintype;
        const explicitBiasTypeRaw = storedActuator.biastype;
        const explicitGainType =
          typeof explicitGainTypeRaw === "string" && explicitGainTypeRaw.trim().length > 0
            ? explicitGainTypeRaw.trim()
            : "";
        const explicitBiasType =
          typeof explicitBiasTypeRaw === "string" && explicitBiasTypeRaw.trim().length > 0
            ? explicitBiasTypeRaw.trim()
            : "";

        if (!xmlJointName) {
          logger.warn(
            `[xmlGenerationActions] Skipping actuator "${exportName}": joint name resolved to empty string.`
          );
          return;
        }
        let actuatorAttrs = `name="${exportName}" joint="${xmlJointName}"`;
        actuatorAttrs = appendPluginAttrs({
          attrs: actuatorAttrs,
          actuator: storedActuator,
          xmlEscapeAttr,
        });

        // If the original actuator used kp/dampratio shorthand (e.g. from MuJoCo Menagerie),
        // expand it into explicit gainprm/biasprm so the generated XML stays compatible with
        // the MuJoCo 2.3.x WASM (kp is fine; dampratio and inheritrange are 3.x-only).
        // kv = 2 * dampratio * sqrt(kp * armature), using the joint's parsed armature.
        const storedKpRaw = storedActuator.kp;
        const storedDampratioRaw = storedActuator.dampratio;
        const storedKp = Number(storedKpRaw);
        const storedDampratio = Number(storedDampratioRaw);
        const storedKv = Number(storedActuator.kv);
        const usePositionShorthand = storedKpRaw !== undefined || storedDampratioRaw !== undefined;

        if (
          storedActuator.forcerange &&
          Array.isArray(storedActuator.forcerange) &&
          storedActuator.forcerange.length === 2
        ) {
          const fr = storedActuator.forcerange.map(Number);
          if (fr.every(Number.isFinite)) {
            actuatorAttrs += ` forcerange="${arrayToString(fr)}"`;
          } else {
            logger.warn(
              `[xmlGenerationActions] Actuator "${exportName}" forcerange contains non-finite values; attribute omitted.`
            );
          }
        }
        if (
          storedActuator.ctrlrange &&
          Array.isArray(storedActuator.ctrlrange) &&
          storedActuator.ctrlrange.length === 2
        ) {
          const cr = storedActuator.ctrlrange.map(Number);
          if (cr.every(Number.isFinite)) {
            actuatorAttrs += ` ctrlrange="${arrayToString(cr)}"`;
          } else {
            logger.warn(
              `[xmlGenerationActions] Actuator "${exportName}" ctrlrange contains non-finite values; attribute omitted.`
            );
          }
        }
        if (
          !usePositionShorthand &&
          mayEmitStoredGainBiasInXml(actuatorTag) &&
          storedActuator.gainprm &&
          Array.isArray(storedActuator.gainprm)
        ) {
          actuatorAttrs += ` gainprm="${arrayToString(storedActuator.gainprm)}"`;
        }
        if (
          !usePositionShorthand &&
          mayEmitStoredGainBiasInXml(actuatorTag) &&
          storedActuator.biasprm &&
          Array.isArray(storedActuator.biasprm)
        ) {
          actuatorAttrs += ` biasprm="${arrayToString(storedActuator.biasprm)}"`;
        }
        if (
          !usePositionShorthand &&
          (actuatorTag === "velocity" || actuatorTag === "intvelocity")
        ) {
          const kvRaw = Number(storedActuator.kv);
          if (Number.isFinite(kvRaw)) {
            actuatorAttrs += ` kv="${formatMjcfNumber(kvRaw)}"`;
          }
        }
        if (!usePositionShorthand && mayEmitStoredGainBiasInXml(actuatorTag)) {
          if (explicitGainType) {
            actuatorAttrs += ` gaintype="${explicitGainType}"`;
          }
          if (explicitBiasType) {
            actuatorAttrs += ` biastype="${explicitBiasType}"`;
          }
          const shouldForceAffineGeneral =
            actuatorTag === "general" &&
            !explicitGainType &&
            !explicitBiasType &&
            ((storedActuator.gainprm && Array.isArray(storedActuator.gainprm)) ||
              (storedActuator.biasprm && Array.isArray(storedActuator.biasprm)));
          if (shouldForceAffineGeneral) {
            // Without explicit class defaults, general actuators need affine bias mode
            // or they behave like raw motors (zero ctrl -> zero torque).
            actuatorAttrs += ` gaintype="fixed" biastype="affine"`;
          }
        }
        const actuatorGear = storedActuator.gear;
        if (Array.isArray(actuatorGear) && actuatorGear.length > 0) {
          actuatorAttrs += ` gear="${arrayToString(actuatorGear)}"`;
        } else if (typeof actuatorGear === "number" && Number.isFinite(actuatorGear)) {
          actuatorAttrs += ` gear="${formatMjcfNumber(actuatorGear)}"`;
        }
        actuatorAttrs = appendActivationDynamicsAttrsForXml(actuatorAttrs, storedActuator);
        if (usePositionShorthand) {
          // Guard kp: ?? does not catch NaN, so explicitly validate finite + positive.
          const kp = Number.isFinite(storedKp) && storedKp > 0 ? storedKp : 50;
          // Compute kv: prefer explicit kv, else derive from dampratio and joint armature.
          //
          // Menagerie models are tuned for `implicitfast` which resolves kv from the full
          // inertia matrix at runtime.  The WASM rewrites to `implicit`, so kv must be
          // explicit.  Armature alone hugely underestimates effective joint inertia, so we
          // scale up by 100x for stability margins matching the UR5e class defaults.
          //
          // Two pitfalls avoided here:
          //  1) The joint's armature property defaults to 0 (not 0.1) in the scene store,
          //     so (joint.armature ?? 0.1) returns 0 ? use Math.max(..., 0.1) instead.
          //  2) storedDampratio may be missing or zero even when kp is set; fall back to
          //     a sensible default (0.95) so kv is never accidentally computed as 0.
          let kv: number;
          if (Number.isFinite(storedKv)) {
            kv = storedKv;
          } else {
            const dampratio =
              storedDampratio != null && storedDampratio > 0 ? storedDampratio : 0.95;
            const armature = Math.max(isScalarJoint(joint) ? joint.armature : 0.1, 0.1);
            kv = 2 * dampratio * Math.sqrt(kp * armature * 100);
          }
          actuatorAttrs += ` gaintype="fixed" biastype="affine"`;
          actuatorAttrs += ` gainprm="${arrayToString([kp, 0, 0])}"`;
          actuatorAttrs += ` biasprm="${arrayToString([0, -kp, -kv])}"`;
        }

        if (usePositionShorthand) {
          // Emit as <general> with explicit gains ? fully compatible with MuJoCo 2.3.x.
          actuatorEntries.push(`    <general ${actuatorAttrs}/>\n`);
        } else {
          const classAttr = resolveActuatorClassAttr(actuatorTag, storedActuator, actuatorClass);
          actuatorEntries.push(`    <${actuatorTag}${classAttr} ${actuatorAttrs}/>\n`);
        }
      });
    });
    // Add a single actuator per detected tendon pair
    tendonPairs.forEach((pair) => {
      // Use the matched actuator from pair detection
      const originalActuator = pair.actuator || tendonActuatorsByName.get(pair.tendonName)?.[0];

      // Use original tendon name
      const tendonName = pair.tendonName;

      // Build actuator attributes
      const joint1Info = jointBySanitizedName[pair.joint1];
      const joint2Info = jointBySanitizedName[pair.joint2];
      const joint1Uuid = joint1Info?.uuid;
      const joint2Uuid = joint2Info?.uuid;
      const tendonRootUuid = joint1Info?.rootUuid || joint2Info?.rootUuid || null;
      const fallbackGeneratedName = generateActuatorControlName({
        baseName: originalActuator?.name || `gripper`,
        jointUuid: joint1Uuid || joint2Uuid || null,
        rootBodyUuid: tendonRootUuid,
        tendonName,
      });
      const exportedName = getUniqueActuatorName(
        getPreferredExportActuatorName(originalActuator || null, fallbackGeneratedName),
        fallbackGeneratedName
      );
      rememberActuatorExportName(originalActuator || null, exportedName, fallbackGeneratedName);

      let actuatorAttrs = `name="${exportedName}" tendon="${tendonName}"`;

      // Preserve original actuator parameters if available
      if (originalActuator) {
        actuatorAttrs = appendPluginAttrs({
          attrs: actuatorAttrs,
          actuator: originalActuator,
          xmlEscapeAttr,
        });
        if (
          originalActuator.forcerange &&
          Array.isArray(originalActuator.forcerange) &&
          originalActuator.forcerange.length === 2
        ) {
          const fr = originalActuator.forcerange.map(Number);
          if (fr.every(Number.isFinite)) {
            actuatorAttrs += ` forcerange="${arrayToString(fr)}"`;
          }
        }
        if (
          originalActuator.ctrlrange &&
          Array.isArray(originalActuator.ctrlrange) &&
          originalActuator.ctrlrange.length === 2
        ) {
          const cr = originalActuator.ctrlrange.map(Number);
          if (cr.every(Number.isFinite)) {
            actuatorAttrs += ` ctrlrange="${arrayToString(cr)}"`;
          }
        }
        const actuatorTagTendonPair = resolveActuatorTagName(originalActuator);
        if (
          mayEmitStoredGainBiasInXml(actuatorTagTendonPair) &&
          originalActuator.gainprm &&
          Array.isArray(originalActuator.gainprm)
        ) {
          actuatorAttrs += ` gainprm="${arrayToString(originalActuator.gainprm)}"`;
        }
        if (
          mayEmitStoredGainBiasInXml(actuatorTagTendonPair) &&
          originalActuator.biasprm &&
          Array.isArray(originalActuator.biasprm)
        ) {
          actuatorAttrs += ` biasprm="${arrayToString(originalActuator.biasprm)}"`;
        }
        if (
          actuatorTagTendonPair === "position" &&
          originalActuator.kp !== undefined &&
          Number.isFinite(Number(originalActuator.kp))
        ) {
          actuatorAttrs += ` kp="${formatMjcfNumber(originalActuator.kp)}"`;
        }
        if (
          (actuatorTagTendonPair === "velocity" || actuatorTagTendonPair === "intvelocity") &&
          originalActuator.kv !== undefined &&
          Number.isFinite(Number(originalActuator.kv))
        ) {
          actuatorAttrs += ` kv="${formatMjcfNumber(originalActuator.kv)}"`;
        }
        const explicitGainTypeRaw = originalActuator.gaintype;
        const explicitBiasTypeRaw = originalActuator.biastype;
        const explicitGainType =
          typeof explicitGainTypeRaw === "string" && explicitGainTypeRaw.trim().length > 0
            ? explicitGainTypeRaw.trim()
            : "";
        const explicitBiasType =
          typeof explicitBiasTypeRaw === "string" && explicitBiasTypeRaw.trim().length > 0
            ? explicitBiasTypeRaw.trim()
            : "";
        if (mayEmitStoredGainBiasInXml(actuatorTagTendonPair)) {
          if (explicitGainType) {
            actuatorAttrs += ` gaintype="${explicitGainType}"`;
          }
          if (explicitBiasType) {
            actuatorAttrs += ` biastype="${explicitBiasType}"`;
          }
        }
        const shouldForceAffineGeneral =
          actuatorTagTendonPair === "general" &&
          !explicitGainType &&
          !explicitBiasType &&
          ((originalActuator.gainprm && Array.isArray(originalActuator.gainprm)) ||
            (originalActuator.biasprm && Array.isArray(originalActuator.biasprm)));
        if (shouldForceAffineGeneral) {
          actuatorAttrs += ` gaintype="fixed" biastype="affine"`;
        }
        actuatorAttrs = appendActivationDynamicsAttrsForXml(actuatorAttrs, originalActuator);
        const classAttr = resolveActuatorClassAttr(
          actuatorTagTendonPair,
          originalActuator,
          "size1"
        );
        actuatorEntries.push(`    <${actuatorTagTendonPair}${classAttr} ${actuatorAttrs}/>\n`);
      } else {
        logger.warn(
          `[XML Gen] Skipping inferred tendon actuator for tendon "${tendonName}" because no explicit actuator definition exists`
        );
      }
    });

    // Also preserve original tendon actuators that were not covered by detected pairs
    // This handles cases where tendons are defined directly in XML without matching equality constraints
    const coveredActuatorNames = new Set(
      tendonPairs
        .map((pair) => pair.actuator?.name)
        .filter((name): name is string => typeof name === "string")
        .map((name) => sanitizeMjcfName(name))
    );

    actuatorsArray.forEach((actuator) => {
      if (!actuator || !actuator.tendon) return;
      const sanitizedName = sanitizeMjcfName(actuator.name);
      if (coveredActuatorNames.has(sanitizedName)) return; // Already handled above

      const rawActuatorTendon = typeof actuator.tendon === "string" ? actuator.tendon : "";
      const actuatorTendonXmlName =
        (actuator.tendonUuid
          ? tendonUuidToEmittedXmlName.get(String(actuator.tendonUuid)) || null
          : null) ||
        (rawActuatorTendon
          ? tendonRefToEmittedXmlName.get(rawActuatorTendon) ||
            tendonRefToEmittedXmlName.get(sanitizeMjcfName(rawActuatorTendon)) ||
            (emittedTendonNames.has(rawActuatorTendon) ? rawActuatorTendon : null) ||
            (emittedTendonNames.has(sanitizeMjcfName(rawActuatorTendon))
              ? sanitizeMjcfName(rawActuatorTendon)
              : null)
          : null);

      // Skip tendon actuators that reference undefined tendons (would cause MuJoCo error)
      if (!actuatorTendonXmlName || !emittedTendonNames.has(actuatorTendonXmlName)) {
        logger.warn(
          `[XML Gen] Skipping tendon actuator "${actuator.name}" - tendon "${actuatorTendonXmlName}" is not defined`
        );
        return;
      }

      // Preserve this original tendon actuator with correct tendon name
      const tendonJointUuid =
        (typeof actuator.joint === "string" && actuator.joint) ||
        jointBySanitizedName[sanitizeMjcfName(actuator.joint || "")]?.uuid ||
        null;
      const tendonRootUuid =
        (tendonJointUuid && jointInfoByUuid.get(tendonJointUuid)?.rootUuid) ||
        jointBySanitizedName[sanitizeMjcfName(actuator.joint || "")]?.rootUuid ||
        null;
      const fallbackGeneratedName = generateActuatorControlName({
        baseName: actuator.name,
        jointUuid: tendonJointUuid,
        rootBodyUuid: tendonRootUuid,
        tendonName: actuatorTendonXmlName || null,
      });
      const tendonExportName = getUniqueActuatorName(
        getPreferredExportActuatorName(actuator || null, fallbackGeneratedName),
        fallbackGeneratedName
      );
      rememberActuatorExportName(actuator || null, tendonExportName, fallbackGeneratedName);
      let actuatorAttrs = `name="${tendonExportName}" tendon="${actuatorTendonXmlName}"`;
      actuatorAttrs = appendPluginAttrs({ attrs: actuatorAttrs, actuator, xmlEscapeAttr });

      if (
        actuator.forcerange &&
        Array.isArray(actuator.forcerange) &&
        actuator.forcerange.length === 2
      ) {
        const fr = actuator.forcerange.map(Number);
        if (fr.every(Number.isFinite)) {
          actuatorAttrs += ` forcerange="${arrayToString(fr)}"`;
        }
      }
      if (
        actuator.ctrlrange &&
        Array.isArray(actuator.ctrlrange) &&
        actuator.ctrlrange.length === 2
      ) {
        const cr = actuator.ctrlrange.map(Number);
        if (cr.every(Number.isFinite)) {
          actuatorAttrs += ` ctrlrange="${arrayToString(cr)}"`;
        }
      }
      const actuatorTagOrphanTendon = resolveActuatorTagName(actuator);
      if (
        mayEmitStoredGainBiasInXml(actuatorTagOrphanTendon) &&
        actuator.gainprm &&
        Array.isArray(actuator.gainprm)
      ) {
        actuatorAttrs += ` gainprm="${arrayToString(actuator.gainprm)}"`;
      }
      if (
        mayEmitStoredGainBiasInXml(actuatorTagOrphanTendon) &&
        actuator.biasprm &&
        Array.isArray(actuator.biasprm)
      ) {
        actuatorAttrs += ` biasprm="${arrayToString(actuator.biasprm)}"`;
      }
      if (
        actuatorTagOrphanTendon === "position" &&
        actuator.kp !== undefined &&
        Number.isFinite(Number(actuator.kp))
      ) {
        actuatorAttrs += ` kp="${formatMjcfNumber(actuator.kp)}"`;
      }
      if (
        (actuatorTagOrphanTendon === "velocity" || actuatorTagOrphanTendon === "intvelocity") &&
        actuator.kv !== undefined &&
        Number.isFinite(Number(actuator.kv))
      ) {
        actuatorAttrs += ` kv="${formatMjcfNumber(actuator.kv)}"`;
      }
      const explicitGainTypeRaw = actuator.gaintype;
      const explicitBiasTypeRaw = actuator.biastype;
      const explicitGainType =
        typeof explicitGainTypeRaw === "string" && explicitGainTypeRaw.trim().length > 0
          ? explicitGainTypeRaw.trim()
          : "";
      const explicitBiasType =
        typeof explicitBiasTypeRaw === "string" && explicitBiasTypeRaw.trim().length > 0
          ? explicitBiasTypeRaw.trim()
          : "";
      if (mayEmitStoredGainBiasInXml(actuatorTagOrphanTendon)) {
        if (explicitGainType) {
          actuatorAttrs += ` gaintype="${explicitGainType}"`;
        }
        if (explicitBiasType) {
          actuatorAttrs += ` biastype="${explicitBiasType}"`;
        }
      }
      const shouldForceAffineGeneral =
        actuatorTagOrphanTendon === "general" &&
        !explicitGainType &&
        !explicitBiasType &&
        ((actuator.gainprm && Array.isArray(actuator.gainprm)) ||
          (actuator.biasprm && Array.isArray(actuator.biasprm)));
      if (shouldForceAffineGeneral) {
        actuatorAttrs += ` gaintype="fixed" biastype="affine"`;
      }
      actuatorAttrs = appendActivationDynamicsAttrsForXml(actuatorAttrs, actuator);
      const classAttr = resolveActuatorClassAttr(actuatorTagOrphanTendon, actuator, "size1");
      actuatorEntries.push(`    <${actuatorTagOrphanTendon}${classAttr} ${actuatorAttrs}/>\n`);
    });
    if (actuatorEntries.length > 0) {
      actuatorXml = "  <actuator>\n";
      actuatorXml += actuatorEntries.join("");
      actuatorXml += "  </actuator>\n\n";
    }
  }

  return actuatorXml;
};

export const emitSensorXml = ({
  sensors,
  jointMap,
  siteMap,
  bodiesMap,
  jointXmlNameMap,
  jointUuidToXmlName,
  siteExportNameByUuid,
  bodyUuidToXmlName,
  tendonRefToEmittedXmlName,
  emittedTendonNames,
  actuatorExportNameByRef,
  reserveUniqueName,
  sanitizeMjcfName,
  xmlEscapeAttr,
  logger,
}: {
  sensors: unknown[];
  jointMap: Record<string, JointExportInfo | undefined>;
  siteMap: Record<string, SiteExportInfo | undefined>;
  bodiesMap: Record<string, BodyExportInfo | undefined>;
  jointXmlNameMap: Record<string, string>;
  jointUuidToXmlName: Record<string, string>;
  siteExportNameByUuid: Record<string, string>;
  bodyUuidToXmlName: Record<string, string>;
  tendonRefToEmittedXmlName: Map<string, string>;
  emittedTendonNames: Set<string>;
  actuatorExportNameByRef: Map<string, string>;
  reserveUniqueName: (
    preferredRaw: string | undefined | null,
    fallbackRaw: string,
    usedNames: Set<string>
  ) => string;
  sanitizeMjcfName: (value: string) => string;
  xmlEscapeAttr: (value: string) => string;
  logger: XmlLogger;
}): string => {
  if (sensors.length === 0) return "";

  const resolveJointSensorRef = (ref: string): string | null => {
    const raw = String(ref || "").trim();
    if (!raw) return null;
    const sanitized = sanitizeMjcfName(raw);
    const direct = jointXmlNameMap[raw] || jointXmlNameMap[sanitized];
    if (direct) return direct;
    const joint = Object.values(jointMap || {}).find((candidate) => {
      if (!candidate) return false;
      return (
        String(candidate.uuid || "") === raw ||
        String(candidate.name || "") === raw ||
        sanitizeMjcfName(String(candidate.name || "")) === sanitized
      );
    });
    return joint?.uuid ? jointUuidToXmlName[String(joint.uuid)] || null : null;
  };

  const resolveSiteSensorRef = (ref: string): string | null => {
    const raw = String(ref || "").trim();
    if (!raw) return null;
    if (siteExportNameByUuid[raw]) return siteExportNameByUuid[raw];
    const sanitized = sanitizeMjcfName(raw);
    const site = Object.values(siteMap || {}).find((candidate) => {
      if (!candidate) return false;
      return (
        String(candidate.uuid || "") === raw ||
        String(candidate.name || "") === raw ||
        sanitizeMjcfName(String(candidate.name || "")) === sanitized
      );
    });
    return site?.uuid ? siteExportNameByUuid[String(site.uuid)] || null : null;
  };

  const resolveBodySensorRef = (ref: string): string | null => {
    const raw = String(ref || "").trim();
    if (!raw) return null;
    if (bodyUuidToXmlName[raw]) return bodyUuidToXmlName[raw];
    const sanitized = sanitizeMjcfName(raw);
    const body = Object.values(bodiesMap || {}).find((candidate) => {
      if (!candidate) return false;
      return (
        String(candidate.uuid || "") === raw ||
        String(candidate.name || "") === raw ||
        sanitizeMjcfName(String(candidate.name || "")) === sanitized
      );
    });
    return body?.uuid ? bodyUuidToXmlName[String(body.uuid)] || null : null;
  };

  const resolveTendonSensorRef = (ref: string): string | null => {
    const raw = String(ref || "").trim();
    if (!raw) return null;
    const sanitized = sanitizeMjcfName(raw);
    return (
      tendonRefToEmittedXmlName.get(raw) ||
      tendonRefToEmittedXmlName.get(sanitized) ||
      (emittedTendonNames.has(raw) ? raw : null) ||
      (emittedTendonNames.has(sanitized) ? sanitized : null)
    );
  };

  const resolveSensorObjectRef = (objtype: string | undefined, ref: string): string | null => {
    const type = String(objtype || "")
      .trim()
      .toLowerCase();
    if (type === "joint") return resolveJointSensorRef(ref);
    if (type === "site") return resolveSiteSensorRef(ref);
    if (type === "body" || type === "xbody") return resolveBodySensorRef(ref);
    if (type === "tendon") return resolveTendonSensorRef(ref);
    if (type === "actuator") {
      return (
        actuatorExportNameByRef.get(ref) ||
        actuatorExportNameByRef.get(sanitizeMjcfName(ref)) ||
        null
      );
    }
    return null;
  };

  let sensorXml = "  <sensor>\n";
  const emittedSensorNames = new Set<string>();
  sensors.forEach((rawSensor) => {
    const sensor = rawSensor as {
      uuid?: string;
      kind?: string;
      name?: string;
      attrs?: Record<string, unknown>;
    };
    if (!sensor?.kind) return;
    const sensorName = xmlEscapeAttr(
      reserveUniqueName(sensor.name, sensor.uuid || sensor.kind, emittedSensorNames)
    );
    let line = `    <${sensor.kind} name="${sensorName}"`;
    Object.entries(sensor.attrs || {})
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .forEach(([key, value]) => {
        let exportValue = String(value);
        if (key === "actuator") {
          const mapped =
            actuatorExportNameByRef.get(exportValue) ||
            actuatorExportNameByRef.get(sanitizeMjcfName(exportValue));
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references actuator "${exportValue}" that was not emitted in the actuator section.`
            );
          }
        } else if (key === "joint") {
          const mapped = resolveJointSensorRef(exportValue);
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references joint "${exportValue}" that was not emitted in the joint section.`
            );
          }
        } else if (key === "site") {
          const mapped = resolveSiteSensorRef(exportValue);
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references site "${exportValue}" that was not emitted in the site section.`
            );
          }
        } else if (key === "body") {
          const mapped = resolveBodySensorRef(exportValue);
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references body "${exportValue}" that was not emitted in the body section.`
            );
          }
        } else if (key === "tendon") {
          const mapped = resolveTendonSensorRef(exportValue);
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references tendon "${exportValue}" that was not emitted in the tendon section.`
            );
          }
        } else if (key === "objname") {
          const objtype =
            typeof sensor.attrs?.objtype === "string" ? sensor.attrs.objtype : undefined;
          const mapped = resolveSensorObjectRef(objtype, exportValue);
          if (mapped) {
            exportValue = mapped;
          } else {
            logger.warn(
              `[FileStore] Sensor "${sensor.name}" references ${objtype || "object"} "${exportValue}" that was not emitted in the XML.`
            );
          }
        }
        line += ` ${key}="${xmlEscapeAttr(exportValue)}"`;
      });
    line += "/>\n";
    sensorXml += line;
  });
  sensorXml += "  </sensor>\n\n";
  return sensorXml;
};
